import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useI18n } from '../i18n';

interface PaymentFormProps {
  onSuccess: (paymentIntentId: string) => void;
  onCancel: () => void;
}

export function PaymentForm({ onSuccess, onCancel }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useI18n();
  const [processing, setProcessing] = useState(false);
  // The PaymentElement mounts asynchronously. Calling confirmPayment() against
  // an element that isn't currently mounted throws an IntegrationError, which
  // previously left the customer stuck on the spinner with no charge and no
  // order. Gate confirmation on the element's `ready` event.
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || !ready || processing) return;

    setProcessing(true);
    setError(null);

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      // 'succeeded' = charged immediately (immediate capture); 'requires_capture'
      // = card authorized and held, to be captured when the restaurant approves
      // (manual capture). Both mean the customer's part is done.
      if (result.error) {
        setError(result.error.message || 'Error');
        setProcessing(false);
      } else if (result.paymentIntent?.status === 'succeeded'
                 || result.paymentIntent?.status === 'requires_capture') {
        onSuccess(result.paymentIntent.id);
      } else {
        setError('Error');
        setProcessing(false);
      }
    } catch (err) {
      // confirmPayment can reject (e.g. the element briefly unmounted during a
      // background-poll re-render). Reset so the customer can retry instead of
      // being dead-ended on a disabled "submitting" button. Report it — this is
      // the class that produced silent, un-actionable checkout failures before.
      Sentry.captureException(err, { tags: { flow: 'stripe-confirm' } });
      setError(err instanceof Error ? err.message : 'Error');
      setProcessing(false);
    }
  }

  return (
    <form className="hc-stripe-form" onSubmit={handleSubmit}>
      <PaymentElement onReady={() => setReady(true)} />
      {error && <div className="hc-form-error">{error}</div>}
      <div className="hc-stripe-actions">
        <button
          type="button"
          className="hc-btn hc-btn-secondary"
          onClick={onCancel}
          disabled={processing}
        >
          {t('checkout.back')}
        </button>
        <button
          type="submit"
          className="hc-btn hc-btn-primary"
          disabled={!stripe || !ready || processing}
        >
          {processing ? t('checkout.submitting') : t('checkout.submit')}
        </button>
      </div>
    </form>
  );
}
